/**
 * Контекст авторизации (Auth Context)
 *
 * Это "хранилище" информации об авторизованном пользователе.
 * Любой компонент может получить доступ к этой информации.
 */

import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import {
  ModuleCode,
  PermissionCode,
  User,
  clearAuthStorage,
  getMyPermissions,
  persistAuthSession,
} from '../services/api';

// ==========================================
// ИНТЕРФЕЙСЫ (типы данных)
// ==========================================

interface AuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  departmentCodes: string[];
  isHead: boolean;
  isAdmin: boolean;
  expensePlanfactArticles: string[];
  can: (moduleCode: ModuleCode, permissionCode: PermissionCode) => boolean;
  hasDepartment: (...codes: string[]) => boolean;
  hasRole: (...names: string[]) => boolean;
  refreshPermissions: () => Promise<void>;
  login: (user: User, accessToken: string, refreshToken: string) => Promise<void>;
  logout: () => void;
}

type PermissionMatrix = Record<ModuleCode, Record<PermissionCode, boolean>>;

const EMPTY_PERMISSIONS: PermissionMatrix = {
  CONTRACTORS: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  CONTRACTS: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  TASKS: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  OPERATIONS: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  OPERATIONS_SYNC: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  ACCESS_ADMIN: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  CONTRACT_SUM: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  CONTRACT_SUM_STAGES: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  CONTRACT_COST: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  CONTRACT_MARGIN: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  CONTRACT_SOURCE_FIRST: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  MASTERDATA: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  INCOME: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  OUTCOME: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  INCOME_PLANNING: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  OUTCOME_REQUEST: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  OUTCOME_ISSUE: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  OUTCOME_CASH: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  OUTCOME_SERV: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  OUTCOME_TAX: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  OUTCOME_PARTNER: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  OUTCOME_PLAN: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  TASKS_CLOSING: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
  CONTRACTS_CLOSING: { VIEW: false, CREATE: false, EDIT: false, DELETE: false, EXPORT: false },
};

// ==========================================
// СОЗДАНИЕ КОНТЕКСТА
// ==========================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ==========================================
// ПРОВАЙДЕР КОНТЕКСТА
// ==========================================

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<PermissionMatrix>(EMPTY_PERMISSIONS);
  const [departmentCodes, setDepartmentCodes] = useState<string[]>([]);
  const [roleNames, setRoleNames] = useState<string[]>([]);
  const [isHead, setIsHead] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [expensePlanfactArticles, setExpensePlanfactArticles] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const applyPermissionsPayload = (data: Awaited<ReturnType<typeof getMyPermissions>>): void => {
    setPermissions({ ...EMPTY_PERMISSIONS, ...data.permissions });
    setDepartmentCodes(data.department_codes ?? []);
    setRoleNames(data.role_names ?? []);
    setIsHead(Boolean(data.is_head));
    setIsAdmin(Boolean(data.is_admin));
    setExpensePlanfactArticles(data.expense_planfact_articles ?? []);
  };

  const clearAccessState = (): void => {
    setPermissions(EMPTY_PERMISSIONS);
    setDepartmentCodes([]);
    setRoleNames([]);
    setIsHead(false);
    setIsAdmin(false);
    setExpensePlanfactArticles([]);
  };

  const refreshPermissions = async (): Promise<void> => {
    try {
      const data = await getMyPermissions();
      applyPermissionsPayload(data);
      const latestAccess = localStorage.getItem('access_token');
      if (latestAccess) {
        setToken(latestAccess);
      }
    } catch (error) {
      clearAccessState();
    }
  };

  // При загрузке приложения проверяем, есть ли сохранённая сессия
  useEffect(() => {
    const initializeAuth = async (): Promise<void> => {
      const savedToken = localStorage.getItem('access_token');
      const savedRefresh = localStorage.getItem('refresh_token');
      const savedUser = localStorage.getItem('user');

      if ((savedToken || savedRefresh) && savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          setToken(savedToken);
          setUser(parsedUser);
          await refreshPermissions();
          const latestAccess = localStorage.getItem('access_token');
          const latestRefresh = localStorage.getItem('refresh_token');
          if (!latestAccess && !latestRefresh) {
            setUser(null);
            setToken(null);
            clearAccessState();
          } else if (latestAccess) {
            setToken(latestAccess);
          }
        } catch (error) {
          console.error('Ошибка при загрузке данных авторизации:', error);
          clearAuthStorage();
          setUser(null);
          setToken(null);
          clearAccessState();
        }
      } else {
        clearAccessState();
      }

      setIsLoading(false);
    };

    initializeAuth();
  }, []);

  // Функция входа — ждём права, чтобы сразу открыть нужную главную
  const login = async (newUser: User, accessToken: string, refreshToken: string): Promise<void> => {
    setUser(newUser);
    setToken(accessToken);
    persistAuthSession(accessToken, refreshToken, newUser);

    try {
      const data = await getMyPermissions();
      applyPermissionsPayload(data);
    } catch {
      clearAccessState();
    }
  };

  // Функция выхода
  const logout = (): void => {
    setUser(null);
    setToken(null);
    clearAccessState();
    clearAuthStorage();
  };

  const can = (moduleCode: ModuleCode, permissionCode: PermissionCode): boolean => {
    return Boolean(permissions[moduleCode]?.[permissionCode]);
  };

  const hasDepartment = (...codes: string[]): boolean => {
    if (isAdmin) return true;
    const wanted = new Set(codes.map((code) => code.trim().toUpperCase()).filter(Boolean));
    if (wanted.size === 0) return false;
    return departmentCodes.some((code) => wanted.has(code.trim().toUpperCase()));
  };

  const hasRole = (...names: string[]): boolean => {
    const wanted = new Set(names.map((name) => name.trim().toLowerCase()).filter(Boolean));
    if (wanted.size === 0) return false;
    return roleNames.some((name) => wanted.has(name.trim().toLowerCase()));
  };

  const value: AuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated: !!token,
    departmentCodes,
    isHead,
    isAdmin,
    expensePlanfactArticles,
    can,
    hasDepartment,
    hasRole,
    refreshPermissions,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ==========================================
// ХУК ДЛЯ ИСПОЛЬЗОВАНИЯ КОНТЕКСТА
// ==========================================

/**
 * Хук для получения данных авторизации из контекста
 * Используй в компонентах так: const { user, logout } = useAuth();
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth должен быть использован внутри AuthProvider');
  }
  return context;
};
